<script setup lang="ts">
import { nextTick, onMounted, reactive, ref } from 'vue';

import { setSubRatings } from '@/api/reviews';
import TextArea from '@/components/base/form/TextArea.vue';
import ThemedButton from '@/components/base/ThemedButton.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import LargeBodyText from '@/components/base/typography/LargeBodyText.vue';
import RatingStars from '@/components/reviews/RatingStars.vue';
import { useCallback } from '@/composables/useCallback';
import { RatingTierWithZero } from '@/lib/reviews';

interface SubRatingsForm {
  flavor: RatingTierWithZero;
  freshness: RatingTierWithZero;
  shipping: RatingTierWithZero;
}

const props = defineProps<{ name: string; orderSkuId: number; token: string }>();

const emit = defineEmits(['displayed-form', 'handle-close']);

const displaySubRatingsForm = ref(false);
const descriptionReview = ref('');
const ratings: SubRatingsForm = reactive({
  flavor: 0,
  freshness: 0,
  shipping: 0,
});

const handleSubmit = useCallback(async () => {
  if (Object.values(ratings).some((value) => value > 0) || descriptionReview.value) {
    const subRatings = {
      flavor: ratings.flavor || undefined,
      freshness: ratings.freshness || undefined,
      shipping: ratings.shipping || undefined,
      message: descriptionReview.value,
    };
    await setSubRatings(props.orderSkuId, { subRatings }, props.token);
  }

  emit('handle-close');
});

onMounted(async () => {
  await nextTick();
  setTimeout(() => {
    displaySubRatingsForm.value = true;
    emit('displayed-form');
  }, 1000);
});
</script>

<template>
  <div class="">
    <transition name="fade" mode="out-in">
      <div v-if="displaySubRatingsForm" class="p-4 md:p-6">
        <LargeBodyText class="text-black">Where did we go wrong?</LargeBodyText>
        <BaseBodyText class="mt-2 text-neutral-500">
          We are always looking for ways to improve. Please take a moment to tell us about your
          experience.
        </BaseBodyText>
        <div class="flex flex-col gap-3 mt-6 sm:mt-8 sm:flex-row">
          <div class="sm:w-1/3">
            <BaseBodyText class="font-semibold text-black">Freshness</BaseBodyText>
            <RatingStars v-model="ratings.freshness" class="mt-1" hideRatingMessage />
          </div>
          <div class="sm:w-1/3">
            <BaseBodyText class="font-semibold text-black">Shipping</BaseBodyText>
            <RatingStars v-model="ratings.shipping" class="mt-1" hideRatingMessage />
          </div>
          <div class="sm:w-1/3">
            <BaseBodyText class="font-semibold text-black">Flavor</BaseBodyText>
            <RatingStars v-model="ratings.flavor" class="mt-1" hideRatingMessage />
          </div>
        </div>
        <BaseBodyText class="mt-4 font-semibold text-black sm:mt-8">
          What can we do to improve?
        </BaseBodyText>
        <TextArea
          v-model="descriptionReview"
          class="mt-2"
          :id="`reviews-description-${orderSkuId}`"
          :label="`Review for ${name}`"
          labelSROnly
          name="reviews-description"
        />
        <div class="flex justify-end gap-2 mt-4 sm:mt-6">
          <ThemedButton class="w-full sm:w-auto h-12" theme="white" @click="emit('handle-close')">
            Exit
          </ThemedButton>
          <ThemedButton
            class="w-full sm:w-auto h-12"
            :isLoading="handleSubmit.isPending"
            theme="gray"
            @click="handleSubmit.execute"
            data-promo="1"
            data-promo-name="Review flow"
            data-promo-creative="Lower rating feedback submitted"
          >
            Submit
          </ThemedButton>
        </div>
      </div>
      <div v-else class="px-4 text-center py-14 sm:px-8">
        <img
          alt=""
          class="object-contain sm:w-32 w-28"
          src="@/assets/reviews/completed-reviews.svg"
        />
        <BaseBodyText class="mt-3">
          Your review of our {{ name }} product has been submitted!
        </BaseBodyText>
      </div>
    </transition>
  </div>
</template>
